import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { GanttTask, ComputedTask, GanttChartProps, TaskPatch } from './types/types';
import { toMid, addDays, diffDays, parseISO } from './dateUtils';
import { WEEK_DAY_W, MONTH_DAY_W, ROW_H, ActionStatus } from './constants';
import GanttLeftPanel from './components/GanttLeftPanel';
import GanttTimeline from './components/GanttTimeline';

const resolveStatus = (task: GanttTask, c: ComputedTask): ActionStatus => {
  if (task.status) return task.status as ActionStatus;
  if (c.progress >= 100) return 'Closed';
  if (c.progress > 0) return 'InProgress';
  return 'Open';
};

// to show weeks instead of days
const startOfWeek = (d: Date) => {
  const x = toMid(d);
  const dow = (x.getDay() + 6) % 7; // 0 = Monday … 6 = Sunday
  return addDays(x, -dow);
};

export default function GanttChart({
  tasks,
  setTasks,
  viewMode = 'months',
  onReorder = () => {},
  onActionClick,
  onPlanClick,
}: GanttChartProps) {
  const [selectedId, setSelectedId] = useState<number | string | null>(null);
  const dayWidth = viewMode === 'weeks' ? WEEK_DAY_W : MONTH_DAY_W;

  const [rowDrag, setRowDrag] = useState<number | string | null>(null);

  const gridScrollRef = useRef<HTMLDivElement>(null);
  const chartScrollRef = useRef<HTMLDivElement>(null);

  const byId = useMemo(() => {
    const m = new Map<number | string, GanttTask>();
    tasks.forEach((t) => m.set(t.id, t));
    return m;
  }, [tasks]);

  const isSummary = useCallback(
    (t: GanttTask) => t.parentId == null,
    []
  );

  // Opens the details (used by the timeline bar only):
  // action details for a leaf task, plan details for a summary (plan) bar.
  const handleSelect = useCallback((id: number | string) => {
    setSelectedId(id);
    const task = byId.get(id);
    if (!task) return;
    if (isSummary(task)) onPlanClick?.(id);
    else onActionClick?.(id);
  }, [byId, isSummary, onActionClick, onPlanClick]);

  // Only highlights the row (used by the left panel) without opening the details.
  const handleRowSelect = useCallback((id: number | string) => {
    setSelectedId(id);
  }, []);

  const computed = useMemo(() => {
  const cache = new Map<number | string, ComputedTask>();

  const compute = (t: GanttTask): ComputedTask => {
    if (cache.has(t.id)) return cache.get(t.id)!;

    const kids = tasks.filter((x) => x.parentId === t.id);
    const cs = kids.map(compute);         

    let start: Date;
    let end: Date;

    if (t.start) {
      start = parseISO(t.start);
      end = addDays(start, t.duration ?? 0);
    } else if (cs.length) {
      start = toMid(new Date(Math.min(...cs.map((c) => +c.start))));
      end = toMid(new Date(Math.max(...cs.map((c) => +c.end))));
    } else {
      start = parseISO(t.start);
      end = addDays(start, t.duration ?? 0);
    }

    const progress = cs.length
      ? (() => {
          const totDur = cs.reduce((a, c) => a + Math.max(c.duration, 0.0001), 0);
          return totDur
            ? Math.round(
                cs.reduce((a, c) => a + c.progress * Math.max(c.duration, 0.0001), 0) / totDur
              )
            : 0;
        })()
      : t.progress ?? 0;

    const res: ComputedTask = {
      start,
      end,
      duration: diffDays(end, start),
      progress,
      kind: cs.length ? 'summary' : 'task',
    };

    cache.set(t.id, res);
    return res;
  };

  const out = new Map<number | string, ComputedTask>();
  tasks.forEach((t) => out.set(t.id, compute(t)));
  return out;
}, [tasks]);

  //  FOR WEEKS
  const { chartStart, dayCount } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    computed.forEach((c) => { min = Math.min(min, +c.start); max = Math.max(max, +c.end); });
    if (!isFinite(min)) { return { chartStart: startOfWeek(new Date()), dayCount: 35 }; }
    const start = startOfWeek(addDays(new Date(min), -1));
    const rawCount = diffDays(new Date(max), start) + 3;
    const count = Math.ceil(Math.max(rawCount, 14) / 7) * 7; // whole weeks
    return { chartStart: start, dayCount: count };
  }, [computed]);

  const xOf = useCallback((date: Date) => diffDays(date, chartStart) * dayWidth, [chartStart, dayWidth]);

  const visibleRows = useMemo(() => {
    const rows: { task: GanttTask; level: number }[] = [];
    const walk = (parentId: number | string | null, level: number) => {
      tasks.filter((t) => t.parentId === parentId).forEach((t) => {
        rows.push({ task: t, level });
        if (isSummary(t) && t.expanded) walk(t.id, level + 1);
      });
    };
    walk(null, 0);
    return rows;
  }, [tasks, isSummary]);

  const rowIndex = useMemo(() => {
    const m = new Map<number | string, number>();
    visibleRows.forEach((r, i) => m.set(r.task.id, i));
    return m;
  }, [visibleRows]);

  const totalW = dayCount * dayWidth;
  const totalH = visibleRows.length * ROW_H;

  const onChartScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (gridScrollRef.current) gridScrollRef.current.scrollTop = e.currentTarget.scrollTop;
    const h = document.getElementById('gantt-head-track');
    if (h) h.style.transform = `translateX(${-e.currentTarget.scrollLeft}px)`;
  };
  const onGridScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (chartScrollRef.current) chartScrollRef.current.scrollTop = e.currentTarget.scrollTop;
  };

  const patch = (id: number | string, p: TaskPatch) =>
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...p } : t)));

  const onRowDrop = (targetTask: GanttTask) => {
    if (rowDrag == null || rowDrag === targetTask.id) { setRowDrag(null); return; }
    const dragged = byId.get(rowDrag);
    if (!dragged) { setRowDrag(null); return; }
    let p = targetTask.parentId;
    while (p != null) {
      if (p === dragged.id) { setRowDrag(null); return; }
      p = byId.get(p)?.parentId ?? null;
    }
    setTasks((prev) => {
      const arr = prev.filter((t) => t.id !== dragged.id);
      const moved: GanttTask = { ...dragged, parentId: targetTask.parentId };
      const idx = arr.findIndex((t) => t.id === targetTask.id);
      arr.splice(idx + 1, 0, moved);
      return arr;
    });
    onReorder(dragged.id, targetTask.id);
    setRowDrag(null);
  };
  
const lastScrolledId = useRef<number | string | null>(null);

const didInitialScroll = useRef(false);

  useEffect(() => {
    didInitialScroll.current = false;
  }, [viewMode]);

  useEffect(() => {
    if (didInitialScroll.current) return;
    const el = chartScrollRef.current;

    if (!el || dayCount === 0 || tasks.length === 0) return;

    const centerOnToday = () => {
      if (!el.clientWidth) return false;
      const x = xOf(toMid(new Date()));
      const maxLeft = Math.max(0, totalW - el.clientWidth);
      el.scrollLeft = Math.min(Math.max(0, x - el.clientWidth / 2), maxLeft);
      didInitialScroll.current = true;
      return true;
    };

    if (centerOnToday()) return;
    const raf = requestAnimationFrame(centerOnToday);
    return () => cancelAnimationFrame(raf);
  }, [dayCount, totalW, xOf, viewMode, tasks.length]);

  useEffect(() => {
    if (selectedId == null || selectedId === lastScrolledId.current) return;
    lastScrolledId.current = selectedId;

    const c = computed.get(selectedId);
    const el = chartScrollRef.current;
    if (!c || !el) return;

    const x = xOf(c.start);
    const barW = Math.max(c.duration, 0.5) * dayWidth;
    const viewLeft = el.scrollLeft;
    const viewRight = viewLeft + el.clientWidth;

    if (x < viewLeft || x + barW > viewRight) {
      el.scrollTo({ left: Math.max(0, x - el.clientWidth / 2), behavior: 'smooth' });
    }
  }, [selectedId, computed, xOf, dayWidth]);

  return (
    <div style={{ fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif', color: '#3c4043', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', height: 620, display: 'flex', fontSize: 13, position: 'relative' }}>
      <GanttLeftPanel
        visibleRows={visibleRows}
        computed={computed}
        selectedId={selectedId}
        isSummary={isSummary}
        resolveStatus={resolveStatus}
        gridScrollRef={gridScrollRef}
        onGridScroll={onGridScroll}
        onSelect={handleRowSelect}
        onToggleExpand={(task) => patch(task.id, { expanded: !task.expanded })}
        onRowDragStart={setRowDrag}
        onRowDrop={onRowDrop}
      />

      <GanttTimeline
        chartStart={chartStart}
        dayCount={dayCount}
        dayWidth={dayWidth}
        viewMode={viewMode}
        visibleRows={visibleRows}
        computed={computed}
        rowIndex={rowIndex}
        xOf={xOf}
        totalW={totalW}
        totalH={totalH}
        selectedId={selectedId}
        isSummary={isSummary}
        resolveStatus={resolveStatus}
        chartScrollRef={chartScrollRef}
        onChartScroll={onChartScroll}
        onSelect={handleSelect}
      />
    </div>
  );
}
